import {
  Button,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  TextField
} from '@blueprinthq/joy'
import {
  Box,
  Flex,
  FormControl,
  HStack,
  ModalBody,
  ModalFooter,
  Text,
  VStack
} from '@chakra-ui/react'
import { useStoreActions, useStoreState } from 'easy-peasy'
import { Field, Form, Formik } from 'formik'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { endpoints } from '../../../api'
import { BPDateTimePicker } from '../../../components'
import { signUpSource } from '../../../constants/enrollment'
import { validationSchema } from '../../../constants/formValidation'
import { usePermissions } from '../../../hooks'
import { queryClient } from '../../../index'
import { generateCopy, renderSubstringAsLink } from '../../../utilities'
import styles from '../quickenroll.module.css'

const defaults = {
  dev_overrideContactInfo: false,
  enrollError: '',
  firstName: '',
  dateOfInvite: ''
}

// This compoment is deprecated and should be removed
export function EnrollmentForm({ setSubmitted }) {
  const [state, setState] = useState(defaults)

  const { user } = useStoreState(state => state.auth)
  const { setSubmittedViewState, setIsQuickEnrollOpen } = useStoreActions(
    actions => actions.quickEnroll
  )

  const { hasPermission } = usePermissions()
  const canAssignOtherClinician = hasPermission(
    'create:clinic:activeclients:enroll'
  )
  const { data: clinicians } = useQuery(
    [endpoints.getClinicClinicians.getCacheId()],
    () =>
      endpoints.getClinicClinicians.request({
        clinicDisplayId: encodeURIComponent(user.clinic.display_id),
        showVisibleOnly: true
      }),
    {
      initialData: { clinicians: [] },
      select: data =>
        data.clinicians.map(c => ({
          id: c.id,
          name: `${c.first_name} ${c.last_name}`
        })),
      enabled: Boolean(user && canAssignOtherClinician)
    }
  )

  const { mutateAsync: enrollClient } = useMutation(
    endpoints.postClinicianEnrollClient.request,
    {
      onSuccess: async response => {
        setSubmittedViewState({
          viewProfileUrl: `/${
            response.data.action_taken.includes('PRE')
              ? 'pre-enroll-patient'
              : 'patient'
          }/${response.data.patient_id}`,
          ...generateCopy(response.data, state.firstName, {
            dateOfInvite: state.dateOfInvite
          })
        })
        setSubmitted(true)
        await queryClient.invalidateQueries(
          endpoints.getPatientList.getCacheId()
        )
        await queryClient.invalidateQueries(
          endpoints.getClinicianUserAccount.getCacheId()
        )
        await queryClient.invalidateQueries(
          endpoints.getPatientCounts.getCacheId()
        )
      },
      onError({ error }) {
        setState(prevState => ({
          ...prevState,
          enrollError:
            error.message ||
            '* There was an error enrolling this client. Please try again'
        }))
      }
    }
  )
  const { enrollError } = state
  const errorMessage = useMemo(() => {
    if (enrollError.length) {
      return (
        <p className={styles.invalid}>
          {renderSubstringAsLink(
            enrollError,
            'contact us',
            process.env.REACT_APP_HELP_EMAIL,
            true
          )}
        </p>
      )
    }
    return null
  }, [enrollError])

  return (
    <Formik
      validationSchema={validationSchema()}
      initialValues={{
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        phoneNumber: '',
        email: '',
        clinician: {
          id: user.id,
          name: `${user.first_name} ${user.last_name}`
        },
        nowOrLater: 'now',
        dateOfInvite: moment().add(1, 'd').format('MM/DD/YYYY hh:mm a'),
        dev_overrideContactInfo: false
      }}
      onSubmit={async (values, actions) => {
        const data = {
          first_name: values.firstName,
          last_name: values.lastName,
          date_of_birth: new Date(values.dateOfBirth),
          phone_number: values.phoneNumber.length
            ? '+1' + values.phoneNumber
            : '',
          email: values.email,
          clinician_id: values.clinician.id,
          date_of_invite:
            values.nowOrLater === 'now'
              ? null
              : moment(values.dateOfInvite, 'MM/DD/YYYY hh:mm a'),
          sign_up_source: signUpSource.CLINICIAN_WEB_QUICK_ENROLL,
          override_contact_info: values.dev_overrideContactInfo
        }
        setState(prevState => ({
          ...prevState,
          firstName: data.first_name,
          dateOfInvite: data.date_of_invite
        }))
        await enrollClient({ data })
        actions.setSubmitting(false)
        actions.resetForm({})
      }}
      initialTouched={{
        dateOfInvite: true
      }}
    >
      {({ values, errors, touched, isSubmitting, isValid, dirty }) => (
        <Form>
          <ModalBody>
            <Box mt="medium" />
            {process.env.REACT_APP_ENV !== 'production' && (
              <>
                <Field name="dev_overrideContactInfo" type="checkbox">
                  {({ field }) => (
                    <FormControl>
                      <Checkbox
                        {...field}
                        isChecked={values.dev_overrideContactInfo}
                        fontWeight="bold"
                      >
                        (DEV ONLY) override pre-existing
                      </Checkbox>
                    </FormControl>
                  )}
                </Field>
                <Box mt="small" />
              </>
            )}
            <VStack spacing="small" alignItems="flex-start">
              <HStack
                pb={
                  (errors.firstName && touched.firstName) ||
                  (errors.lastName && touched.lastName)
                    ? 'small'
                    : '0'
                }
                spacing="small"
                width="100%"
              >
                <Field name="firstName">
                  {({ field }) => (
                    <TextField
                      {...field}
                      autoFocus
                      label="First Name"
                      isInvalid={errors.firstName && touched.firstName}
                      errorText={errors.firstName}
                      isRequired
                    />
                  )}
                </Field>
                <Field name="lastName">
                  {({ field }) => (
                    <TextField
                      {...field}
                      label="Last Name"
                      isInvalid={errors.lastName && touched.lastName}
                      errorText={errors.lastName}
                      isRequired
                    />
                  )}
                </Field>
              </HStack>
              <HStack
                pb={
                  (errors.dateOfBirth && touched.dateOfBirth) ||
                  (errors.phoneNumber && touched.phoneNumber)
                    ? 'small'
                    : '0'
                }
                spacing="small"
                width="100%"
              >
                <Field name="dateOfBirth">
                  {({ field }) => (
                    <BPDateTimePicker
                      {...field}
                      showDate
                      isRequired
                      label="Date of Birth"
                      isInvalid={errors.dateOfBirth && touched.dateOfBirth}
                      errorText={
                        (errors.dateOfBirth &&
                          errors.dateOfBirth.includes('Invalid') &&
                          'Invalid date') ||
                        errors.dateOfBirth
                      }
                    />
                  )}
                </Field>
                <Field name="phoneNumber">
                  {({ field }) => (
                    <TextField
                      {...field}
                      label="Phone"
                      isInvalid={errors.phoneNumber && touched.phoneNumber}
                      errorText={errors.phoneNumber}
                      type="tel"
                      leftIcon={'+1'}
                    />
                  )}
                </Field>
              </HStack>
              <HStack
                pb={errors.email && touched.email ? 'small' : '0'}
                w="100%"
              >
                <Field name="email">
                  {({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      isInvalid={errors.email && touched.email}
                      errorText={errors.email}
                    />
                  )}
                </Field>
              </HStack>
              {canAssignOtherClinician && (
                <Field name="clinician">
                  {({ field, form }) => (
                    <Select
                      {...field}
                      onChange={value => form.setFieldValue('clinician', value)}
                      options={clinicians}
                      valueKey="id"
                      labelKey="name"
                      label="Clinician"
                      isInvalid={errors.clinician && touched.clinician}
                      errorText={errors.clinician}
                      isRequired
                    />
                  )}
                </Field>
              )}
            </VStack>
            <Box mt="medium">
              <Text>
                When do you want us to send this client an invitation to start
                using Blueprint?
              </Text>
            </Box>
            <Box mt="small" />
            <Field name="nowOrLater">
              {({ field, form }) => (
                <RadioGroup
                  {...field}
                  stackProps={{ spacing: 'large' }}
                  defaultValue="now"
                  onChange={value => {
                    form.setFieldValue('nowOrLater', value)
                    if (value === 'now') {
                      form.setFieldValue(
                        'dateOfInvite',
                        moment().add(1, 'd').format('MM/DD/YYYY hh:mm a')
                      )
                    }
                  }}
                >
                  <Radio value="now">Now</Radio>
                  <Radio value="later">Later</Radio>
                </RadioGroup>
              )}
            </Field>
            {values.nowOrLater === 'later' && (
              <Box mt="medium">
                <HStack
                  pb={
                    errors.dateOfInvite && touched.dateOfInvite ? 'small' : '0'
                  }
                  w="100%"
                >
                  <Field name="dateOfInvite" id="enrollment-date-picker">
                    {({ field, form }) => (
                      <BPDateTimePicker
                        {...field}
                        showDate
                        showTime
                        form={form}
                        fieldName={field.name}
                        isInvalid={errors.dateOfInvite && touched.dateOfInvite}
                        errorText={
                          (errors.dateOfInvite &&
                            errors.dateOfInvite.includes('Invalid') &&
                            'Invalid date') ||
                          errors.dateOfInvite
                        }
                      />
                    )}
                  </Field>
                </HStack>
              </Box>
            )}
            <Box mt="small">{errorMessage}</Box>
          </ModalBody>
          <ModalFooter pt="0">
            <Flex
              flex={1}
              flexDirection={'column'}
              justify={'space-between'}
              align={'center'}
            >
              <Button
                size="lg"
                isFullWidth
                isLoading={isSubmitting}
                isDisabled={!isValid || !dirty}
                type="submit"
              >
                Submit
              </Button>
              <Button
                variant="text"
                as={Link}
                className={styles.enrollLink}
                to={{
                  pathname: '/patients/enroll',
                  state: {
                    phone: values.phoneNumber,
                    first_name: values.firstName,
                    last_name: values.lastName,
                    email: values.email,
                    dobISO: values.dateOfBirth,
                    dev_overrideContactInfo: values.dev_overrideContactInfo,
                    nowOrLater: values.nowOrLater,
                    dateOfInvite: values.dateOfInvite
                  }
                }}
                onClick={() => setIsQuickEnrollOpen(false)}
                mt="small"
              >
                Customize assessment plan ➝
              </Button>
            </Flex>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  )
}
